import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, User, Phone, Building, ArrowLeft, IdCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { signupUser } from "@/services/user";
import { useAuth } from "@/context/AuthContext";
import { normalizePhoneNumber, isValidPhoneNumber } from "@/utils/phone";
import { countryCodes } from "@/constants";


import { z } from "zod";

const signupSchema = z.object({
  fullName: z.string(),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().optional(),
  phone: z.string().transform(val => normalizePhoneNumber(val)).refine(val => isValidPhoneNumber(val), {
    message: "Phone number must include country code (e.g., +971501234567)"
  }),
  licenseNumber: z.string().min(1, "License number is required"),   // ✔ NEW VALIDATION
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const BrokerSignup = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState<string>(countryCodes[0].value);
  const [company, setCompany] = useState("");
  const [licenseNumber, setLicenseNumber] = useState(""); // ✔ NEW STATE
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = {
      fullName,
      email,
      phone: countryCode + phone, // Combine country code and phone
      company,
      licenseNumber,   // ✔ INCLUDE IN FORM DATA
      password,
      confirmPassword,
    };

    const result = signupSchema.safeParse(formData);

    if (!result.success) {
      const error = result.error.errors[0];
      toast({
        title: "Validation Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { token, user } = await signupUser({
        name: fullName,
        email,
        password,
        phone: result.data.phone, // Use normalized phone from Zod result
        company,
        licenseNumber,     // ✔ SEND TO BACKEND
        userType: "BROKER",
      });

      login(token, user);

      toast({
        title: "Account Created Successfully",
        description: "Welcome to PropVerse!",
      });

      navigate("/broker/dashboard");
    } catch (error: any) {
      toast({
        title: "Signup Failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container max-w-screen-xl mx-auto px-4 py-12 min-h-screen flex items-center justify-center relative">
      <Link
        to="/"
        className="absolute top-12 left-0 flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Broker Signup</CardTitle>
          <CardDescription className="text-center">
            Create an account to list and manage properties
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSignup}>
          <CardContent className="space-y-4">

            {/* Full Name */}
            <div className="space-y-2">
              <Label>Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10" value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="John Doe" />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input type="email" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="john@gmail.com" />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger className="w-[110px]">
                    <SelectValue placeholder="Code" />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodes.map((code) => (
                      <SelectItem key={code.label} value={code.value}>
                        {code.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input type="tel" className="pl-10" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+971501234567" />
                </div>
              </div>
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label>Company (Optional)</Label>
              <div className="relative">
                <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company Name" />
              </div>
            </div>

            {/* 🔥 License Number (NEW FIELD) */}
            <div className="space-y-2">
              <Label>Broker License Number</Label>
              <div className="relative">
                <IdCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-10"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  required
                  placeholder="BRN-12345"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input type={showPassword ? "text" : "password"} className="pl-10" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-10 w-10" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff /> : <Eye />}
                </Button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input type={showPassword ? "text" : "password"} className="pl-10" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
              </div>
            </div>

          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
            <div className="text-center text-sm">
              Already have an account?
              <Link to="/broker/login" className="text-primary hover:underline ml-1">
                Login
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default BrokerSignup;
